import { ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { useRestaurantStore } from "@/stores/restaurant"
import { playNotificationSound } from "@/services/notificationSound"
import { webSocketService } from "@/services/websocket"

// Singleton state - shared across all instances
const pollingInterval = ref<ReturnType<typeof setInterval> | null>(null)
const POLLING_INTERVAL_MS = 15000 // 15 seconds
let isInitialized = false
let lastActiveOrderIds: string[] = []

export const useActiveOrdersPolling = () => {
  const restaurantStore = useRestaurantStore()
  const { restaurant } = storeToRefs(restaurantStore)
  const { fetchActiveOrders } = restaurantStore

  const startPolling = () => {
    // Clear existing polling if any
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value)
    }

    // Only start if restaurant is available
    if (!restaurant.value?.id) {
      return
    }

    // eslint-disable-next-line no-console
    console.log("🔄 [ActiveOrdersPolling] Starting fallback polling (websocket disconnected)")

    pollingInterval.value = setInterval(async () => {
      if (restaurant.value?.id) {
        try {
          // Capture previous active order IDs before refresh
          const previousIds = [...(restaurantStore.activeOrders || [])].map(
            order => order.id
          )

          // eslint-disable-next-line no-console
          console.log("🔄 [ActiveOrdersPolling] Polling for active orders...")
          await fetchActiveOrders(restaurant.value.id)

          const currentIds = [...(restaurantStore.activeOrders || [])].map(
            order => order.id
          )

          // Detect newly appeared orders compared to last snapshot
          const newOrderIds = currentIds.filter(
            id => !previousIds.includes(id) && !lastActiveOrderIds.includes(id)
          )

          if (newOrderIds.length > 0) {
            // eslint-disable-next-line no-console
            console.log(
              "🔔 [ActiveOrdersPolling] Detected new active orders during polling:",
              newOrderIds
            )
            playNotificationSound()
          }

          lastActiveOrderIds = currentIds
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("❌ [ActiveOrdersPolling] Error during polling:", error)
        }
      }
    }, POLLING_INTERVAL_MS)
  }

  const stopPolling = () => {
    if (pollingInterval.value) {
      // eslint-disable-next-line no-console
      console.log("🛑 [ActiveOrdersPolling] Stopping fallback polling (websocket connected)")
      clearInterval(pollingInterval.value)
      pollingInterval.value = null
    }
  }

  // Initialize only once (singleton pattern)
  if (!isInitialized) {
    isInitialized = true

    // eslint-disable-next-line no-console
    console.log("🔄 [ActiveOrdersPolling] Polling service initialized")

    // Watch websocket connection status
    watch(
      () => webSocketService.socket?.connected,
      (isConnected) => {
        if (isConnected) {
          stopPolling()
        } else {
          // Only start polling if restaurant is available
          if (restaurant.value?.id) {
            startPolling()
          }
        }
      },
      { immediate: true }
    )

    // Watch for restaurant changes and update polling
    watch(
      restaurant,
      (newRestaurant) => {
        if (newRestaurant?.id) {
          // If websocket is not connected, start polling
          if (!webSocketService.socket?.connected) {
            startPolling()
          }
        } else {
          stopPolling()
        }
      },
      { immediate: true }
    )
  }

  return {
    // No public API needed - polling is automatic
  }
}
